import Button from '../ui/Button';
import Badge from '../ui/Badge';
import type { NotificationChannel } from '../../types/notifications';
import { CHANNEL_TYPE_ICONS, CHANNEL_TYPE_LABELS } from '../../types/notifications';

interface NotificationChannelListProps {
  channels: NotificationChannel[];
  onEdit: (channel: NotificationChannel) => void;
  onDelete: (id: string) => void;
  onVerify: (channel: NotificationChannel) => void;
  onTest: (channel: NotificationChannel) => void;
}

export function NotificationChannelList({
  channels,
  onEdit,
  onDelete,
  onVerify,
  onTest,
}: NotificationChannelListProps) {
  if (!channels || channels.length === 0) {
    return (
      <div className="p-8 text-center">
        <p className="text-muted-foreground">No notification channels configured</p>
        <p className="text-sm text-muted-foreground mt-1">
          Add a channel to start receiving notifications
        </p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-border">
      {channels.map((channel) => (
        <div key={channel.id} className="p-4 hover:bg-muted">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="text-2xl">
                {CHANNEL_TYPE_ICONS[channel.channel_type]}
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <h3 className="text-sm font-medium text-foreground">
                    {channel.label}
                  </h3>
                  <Badge
                    variant={channel.is_active ? 'success' : 'neutral'}
                    size="sm"
                  >
                    {channel.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  {!channel.is_verified && (
                    <Badge variant="warning" size="sm">
                      Unverified
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {CHANNEL_TYPE_LABELS[channel.channel_type]}: {channel.channel_value}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {!channel.is_verified && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onVerify(channel)}
                >
                  Verify
                </Button>
              )}
              {channel.is_verified && channel.is_active && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onTest(channel)}
                >
                  Test
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => onEdit(channel)}
              >
                Edit
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onDelete(channel.id)}
                className="text-destructive hover:text-destructive/80"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
} 